import {Component, Input, OnInit} from '@angular/core';
import {UnsubscribeBaseComponent} from "../../../unsubscribe-base.component";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import {WorkflowInstanceModel} from "../../../../models/workflow/workflow-instance.model";
import {Workflow} from 'src/app/enums/Workflow';
import {WorkflowStep} from 'src/app/enums/WorkflowStep';
import {BatchModel} from "../../../../models/batch/batch.model";
import {debounceTime, takeUntil} from "rxjs";
import {BatchType} from "../../../../enums/BatchType";
import {WorkflowInstanceService} from "../../../../services/workflow-instance.service";
import {FileService} from "../../../../services/file.service";
import {MessageService} from "../../../../services/message.service";
import {WorkflowDetailClientService} from "../../../dialog/workflow-detail-dialog/workflow-detail-client.service";
import {WorkflowStepLibraryQubitModel} from "../../../../models/workflow-step/workflow-step-library-qubit.model";
import {BatchUtils} from "../../../../utils/batch.utils";
import {WorkflowInstanceStepLibraryQualityAssuranceQubitService} from "../../../../services/workflow-instance-step-library-quality-assurance-qubit.service";

@Component({
  selector: 'app-library-qubit',
  templateUrl: './library-qubit.component.html',
  styleUrls: ['./library-qubit.component.scss']
})
export class LibraryQubitComponent extends UnsubscribeBaseComponent implements OnInit {

  Workflow = Workflow;
  WorkflowStep = WorkflowStep;
  private isInitializing: boolean = true;

  readonly AD = 'ad';
  readonly DILUTION = 'dilution';
  readonly CONCENTRATION = 'concentration';
  readonly CONCENTRATION_1 = 'concentration1';
  readonly CONCENTRATION_2 = 'concentration2';

  concentrationForm: FormArray;

  @Input() workflowInstance: WorkflowInstanceModel;
  @Input() workflowStep: WorkflowStepLibraryQubitModel;
  @Input() readOnly: boolean = false;

  batchFormArray: UntypedFormArray;
  batchTypes: BatchType[] = [];

  constructor(private workflowInstanceService: WorkflowInstanceService,
              private fileService: FileService,
              private messageService: MessageService,
              public workflowDetailClientService: WorkflowDetailClientService,
              private libraryQubitService: WorkflowInstanceStepLibraryQualityAssuranceQubitService) {
    super();
  }

  ngOnInit() {
    this.initConcentrationForm();
    this.batchTypes = BatchType.getAllForQubit();
    this.batchFormArray = BatchUtils.initBatchFormArray(this.workflowStep.batches, this.batchTypes, this.readOnly);
    this.validateStep();
    // load initial values for concentration
    if (!this.readOnly) {
      this.calculateVolumesFromBackend();
    }
    this.isInitializing = false;
  }

  private initConcentrationForm(): void {
    this.concentrationForm = new FormArray([]);
    this.workflowStep.concentrations.forEach(concentration => {
      const formGroup = new FormGroup({
        id: new FormControl(concentration.id),
        ad: new FormControl(concentration.ad),
        dilution: new FormControl(concentration.dilution),
        concentration: new FormControl(concentration.concentration),
        concentration1: new FormControl(concentration.concentration1, [Validators.required]),
        concentration2: new FormControl(concentration.concentration2, [Validators.required])
      });
      this.concentrationForm.push(formGroup);

      const handleConcentrationChange = () => {
        const c1 = formGroup.get(this.CONCENTRATION_1).value;
        const c2 = formGroup.get(this.CONCENTRATION_2).value;
        const foundConcentration = this.workflowStep.concentrations.find(c => c.id === formGroup.get('id').value);

        if (foundConcentration) {
          foundConcentration.concentration1 = c1;
          foundConcentration.concentration2 = c2;

          if (c1 != null && c2 != null && c1 > 0 && c2 > 0) {
            const mean = Math.round(((Number(c1) + Number(c2)) / 2) * 100) / 100;
            foundConcentration.concentration = mean;
            formGroup.get(this.CONCENTRATION).setValue(mean, {emitEvent: false});

            if (!this.readOnly) {
              this.calculateVolumesFromBackend();
            }
          } else {
            foundConcentration.concentration = null;
            formGroup.get(this.CONCENTRATION).setValue(0, {emitEvent: false});
            formGroup.get(this.AD).setValue(0, {emitEvent: false});
          }
        }
        this.validateStep();
      };

      formGroup.get(this.CONCENTRATION_1).valueChanges
        .pipe(
          takeUntil(this.unSubscribe),
          debounceTime(500)
        )
        .subscribe(() => handleConcentrationChange());

      formGroup.get(this.CONCENTRATION_2).valueChanges
        .pipe(
          takeUntil(this.unSubscribe),
          debounceTime(500)
        )
        .subscribe(() => handleConcentrationChange());
    });
  }

  private calculateVolumesFromBackend(): void {
    if (!this.concentrationForm || !this.workflowStep) {
      return;
    }

    this.libraryQubitService.calculateVolumes(this.workflowStep)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe({
        next: (result) => {
          // Update the form values with the calculated values
          for (let i = 0; i < this.concentrationForm.length; i++) {
            const formGroup = this.concentrationForm.at(i) as FormGroup;
            const concentrationId = formGroup.get('id').value;
            const foundConcentration = result.concentrations.find(c => c.id === concentrationId);

            if (foundConcentration) {
              formGroup.get(this.DILUTION).setValue(foundConcentration.dilution);
              formGroup.get(this.AD).setValue(foundConcentration.ad);
              formGroup.get(this.CONCENTRATION).setValue(foundConcentration.concentration, {emitEvent: false});
            }
          }

          this.workflowStep.concentrations = result.concentrations;
          this.workflowStep.volumeForFourNmPool = result.volumeForFourNmPool;
          this.workflowStep.targetConcentration = result.targetConcentration;
        },
        error: (error) => {
          console.error('Error calculating volumes:', error);
          this.messageService.addErrorMessage('Die Volumina konnten nicht berechnet werden.', 'Fehler bei der Berechnung');
        }
      });
  }

  getTotalVolume(): number {
    return (this.workflowStep.concentrations?.reduce((sum, c) => sum + (c.dilution || 0), 0) || 0) + this.getTotalWater();
  }

  getTotalWater(): number {
    return this.workflowStep.concentrations?.reduce((sum, c) => sum + (c.ad || 0), 0) || 0;
  }

  getPoolLabel(index: number): string {
    return ['A', 'B', 'C', 'D'][index];
  }

  getFormGroupFromAbstractControl(form: AbstractControl): UntypedFormGroup {
    return form as UntypedFormGroup;
  }

  onIndexPoolCombined() {
    this.workflowStep.indexPoolsCombined = true;
    this.workflowDetailClientService.isStepValid$.next(this.workflowStep.isValid());

    if (!this.readOnly) {
      this.workflowDetailClientService.silentSave();
    }
  }

  validateStep(): void {
    this.workflowStep.batches = BatchModel.getBatchesFromBatchSelectionFormArray(this.batchFormArray);
    this.workflowDetailClientService.isStepValid$.next(this.workflowStep.isValid());

    // Save the current state when batch changes to prevent data loss
    if (!this.readOnly && !this.isInitializing) {
      this.workflowDetailClientService.silentSave();
    }
  }

}
